/**
 * Playbook runner — drives an autopilot sequence with cinematic pacing (spec §18).
 * Sequential; each step mutates the canvas via the SAME operator/stage/court-safe
 * paths a human would use. A monotonic token cancels a run (Stop / re-run).
 */
import type cytoscape from 'cytoscape';
import { useFusionStore } from '../stores/useFusionStore';
import { OPERATORS } from './registry';
import { runOperator } from './jobs';
import { NCRP_FIVE_ACT, type PBStep } from './playbooks';

let token = 0;
let jseq = 0;
const cy = () => (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export function isPlaybookRunning(): boolean {
  return !!useFusionStore.getState().pb?.running;
}

export function stopPlaybook() {
  token++;
  useFusionStore.getState().setPb(null);
}

function runStep(s: PBStep) {
  const store = useFusionStore.getState();
  switch (s.kind) {
    case 'stage':
      store.setPlaying(false);
      store.setStep(s.n ?? 1);
      break;
    case 'focus': {
      const c = cy(); const n = c?.getElementById(s.nodeId ?? '');
      if (c && n && !n.empty()) { c.animate({ center: { eles: n } }, { duration: 300 }); store.select({ id: s.nodeId!, kind: 'node' }); }
      break;
    }
    case 'operator': {
      const op = OPERATORS.find((o) => o.id === s.opId);
      if (op && s.nodeId) runOperator(op, { nodeId: s.nodeId, nodeType: '' });
      break;
    }
    case 'job':
      store.addJob({ id: `pb-${Date.now()}-${jseq++}`, operator: (s.label || 'job').split(' ')[0], label: s.label || '', status: 'done', message: s.message, ts: Date.now() });
      break;
    case 'courtSafe':
      if (store.courtSafe !== !!s.on) store.toggleCourtSafe();
      break;
  }
}

export async function runPlaybook() {
  const my = ++token;
  const store = useFusionStore.getState();
  store.clearJobs();
  store.setPlaying(false);
  if (store.courtSafe) store.toggleCourtSafe();

  const acts = NCRP_FIVE_ACT;
  for (let i = 0; i < acts.length; i++) {
    if (token !== my) return;
    const act = acts[i];
    useFusionStore.getState().setPb({ running: true, title: act.title, sub: act.sub, actIndex: i + 1, totalActs: acts.length });
    for (const step of act.steps) {
      if (token !== my) return;
      runStep(step);
      await wait(step.hold ?? 1200);
    }
  }
  if (token !== my) return;
  useFusionStore.getState().setPb({ running: false, title: 'Investigation complete', sub: 'Court-safe case package ready to export', actIndex: acts.length, totalActs: acts.length });
  await wait(3200);
  if (token === my) useFusionStore.getState().setPb(null);
}
